import React, { useState } from 'react';
import './FeelBucketItem.css';
import { TimeOptions, TimeOptionsLabel } from './constants';

const EMOTIONS = [
    { value: 'satisfied', label: '😊 Satisfied', color: '#2dc84d', status: 'green' },
    { value: 'neutral', label: '😐 Neutral', color: '#c1c1c1', status: 'white' },
    { value: 'stressed', label: '😰 Stressed', color: '#f7ea48', status: 'yellow' },
    { value: 'neglected', label: '😔 Neglected', color: '#f7ea48', status: 'yellow' },
    { value: 'urgent', label: '🔥 Urgent', color: '#e03c31', status: 'red' },
    { value: 'excited', label: '✨ Excited', color: '#753bbd', status: 'purple' },
    { value: 'done', label: '👍 Done for Now', color: '#2196F3', status: 'blue' }
];

const ACTION_STATUS_OPTIONS = [
    { value: 'not-started', label: '⚪ Not Started', color: '#c1c1c1', actionStatus: 'white' },
    { value: 'in-progress', label: '🟡 In Progress', color: '#f7ea48', actionStatus: 'yellow' },
    { value: 'blocked', label: '🔴 Blocked', color: '#e03c31', actionStatus: 'red' },
    { value: 'skipped', label: '⏭️ Skipped', color: '#753bbd', actionStatus: 'purple' },
    { value: 'completed', label: '✅ Completed', color: '#2196F3', actionStatus: 'blue' }
];

const DIFFICULTY_OPTIONS = [
    { value: 'very-easy', label: '😌 Very Easy', color: '#2196F3', status: 'blue' },
    { value: 'easy', label: '😊 Easy', color: '#753bbd', status: 'purple' },
    { value: 'moderately-easy', label: '🙂 Moderately Easy', color: '#2dc84d', status: 'green' },
    { value: 'moderate', label: '😐 Moderate', color: '#c1c1c1', status: 'white' },
    { value: 'hard', label: '😰 Hard', color: '#f7ea48', status: 'yellow' },
    { value: 'very-hard', label: '😤 Very Hard', color: '#e03c31', status: 'red' }
];

export const FeelBucketItem = ({ bucket, currentDate, feelEntry, onUpdate, onBucketUpdate, onActionStatusUpdate, onPointsUpdate, onDifficultyUpdate, onScheduleUpdate }) => {
    const [priority, setPriority] = useState(feelEntry?.priority || 5);
    const [emotion, setEmotion] = useState(feelEntry?.emotion || '');
    const [actionStatus, setActionStatus] = useState(feelEntry?.actionStatus || '');
    const [points, setPoints] = useState(bucket?.points || 0);
    const [difficultyLevel, setDifficultyLevel] = useState(feelEntry?.difficultyLevel || '');
    const [preferredTimes, setPreferredTimes] = useState(feelEntry?.preferredTimes || []);
    const [currentStep, setCurrentStep] = useState(0);
    const [isCollapsed, setIsCollapsed] = useState(false);

    const handleEmotionSelect = (emotionValue) => {
        setEmotion(emotionValue);
        onUpdate({
            priority,
            emotion: emotionValue,
            actionStatus,
            difficultyLevel,
            preferredTimes
        });

        // Update bucket status based on emotion
        const selectedEmo = EMOTIONS.find(e => e.value === emotionValue);
        if (selectedEmo && onBucketUpdate) {
            onBucketUpdate(bucket.rowID, selectedEmo.status);
        }

        // Move to next step
        setCurrentStep(1);
    };

    const handleActionStatusSelect = (statusValue) => {
        setActionStatus(statusValue);
        onUpdate({
            priority,
            emotion,
            actionStatus: statusValue,
            difficultyLevel,
            preferredTimes,
        })

        // Update bucket actionStatus based on selection
        const selectedStatus = ACTION_STATUS_OPTIONS.find(s => s.value === statusValue);
        if (selectedStatus && onBucketUpdate) {
            // Find the bucket and update its actionStatus field
            const updatedBucket = {
                ...bucket,
                actionStatus: selectedStatus.actionStatus
            };
            // We'll need to pass this through a different callback
            if (onActionStatusUpdate) {
                onActionStatusUpdate(bucket.rowID, selectedStatus.actionStatus);
            }
        }

        // Move to next step
        setCurrentStep(2);
    };

    const handleSetPoints = () => {
        if (onPointsUpdate) {
            onPointsUpdate(bucket.rowID, points);
        }
        // Move to next step
        setCurrentStep(3);
    };

    const handleTimeToggle = (timeOption) => {
        const newPreferredTimes = preferredTimes.includes(timeOption)
            ? preferredTimes.filter(t => t !== timeOption)
            : [...preferredTimes, timeOption];

        setPreferredTimes(newPreferredTimes);
    };

    const handleUpdateSchedule = () => {
        onUpdate({
            priority,
            emotion,
            actionStatus,
            difficultyLevel,
            preferredTimes
        });

        // Update bucket time schedule
        if (onScheduleUpdate) {
            onScheduleUpdate(bucket.rowID, preferredTimes);
        }
        // Collapse after updating schedule
        setIsCollapsed(true);

    };

    const handleDifficultySelect = (difficultyValue) => {
        setDifficultyLevel(difficultyValue);
        onUpdate({
            priority,
            emotion,
            actionStatus,
            difficultyLevel: difficultyValue,
            preferredTimes
        });
    };

    const handleSetDifficulty = () => {
        // Update bucket difficulty with the color status
        const selectedDifficulty = DIFFICULTY_OPTIONS.find(d => d.value === difficultyLevel);
        if (selectedDifficulty && onDifficultyUpdate) {
            onDifficultyUpdate(bucket.rowID, selectedDifficulty.status);
        }
        // Move to next step
        setCurrentStep(4);
    };

    const selectedEmotion = EMOTIONS.find(e => e.value === emotion);

    // If collapsed, show compact view
    if (isCollapsed) {
        return (
            <div className="feel-bucket-item feel-bucket-collapsed">
                <div className="feel-bucket-header">
                    <div className="feel-bucket-info">
                        <img
                            src={`/images/${bucket.icon}`}
                            alt={bucket.name}
                            className="feel-bucket-icon"
                            onError={(e) => { e.target.src = '/images/A1-Mind.svg'; }}
                        />
                        <div className="feel-bucket-name">{bucket.name}</div>
                        <span className="checked-in-badge">✓ Checked In</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div className="feel-bucket-category">{bucket.category}</div>
                        <button
                            className="edit-feel-button"
                            onClick={() => {
                                setIsCollapsed(false);
                                setCurrentStep(0);
                            }}
                            title="Edit responses"
                        >
                            ✏️
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="feel-bucket-item">
            <div className="feel-bucket-header">
                <div className="feel-bucket-info">
                    <img
                        src={`/images/${bucket.icon}`}
                        alt={bucket.name}
                        className="feel-bucket-icon"
                        onError={(e) => { e.target.src = '/images/A1-Mind.svg'; }}
                    />
                    <div className="feel-bucket-name">{bucket.name}</div>
                </div>
                <div className="feel-bucket-category">{bucket.category}</div>
            </div>

            <div className="feel-controls">{currentStep === 0 && (
                /* Emotion Selector */
                <div className="emotion-section">
                    <label className="emotion-label">How do you feel about this?</label>
                    <div className="emotion-grid">
                        {EMOTIONS.map((emo) => (
                            <button
                                key={emo.value}
                                className={`emotion-button ${emotion === emo.value ? 'selected' : ''} ${emo.value === 'done' ? 'done-button' : ''}`}
                                onClick={() => handleEmotionSelect(emo.value)}
                                style={{
                                    borderColor: emotion === emo.value ? emo.color : '#ccc',
                                    backgroundColor: emotion === emo.value ? `${emo.color}20` : 'white'
                                }}
                            >
                                {emo.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

                {currentStep === 1 && (
                    /* Action Status Selector */
                    <div className="emotion-section">
                        <label className="emotion-label">How is the action status for the day?</label>
                        <div className="emotion-grid">
                            {ACTION_STATUS_OPTIONS.map((status) => (
                                <button
                                    key={status.value}
                                    className={`emotion-button ${actionStatus === status.value ? 'selected' : ''}`}
                                    onClick={() => handleActionStatusSelect(status.value)}
                                    style={{
                                        borderColor: actionStatus === status.value ? status.color : '#ccc',
                                        backgroundColor: actionStatus === status.value ? `${status.color}20` : 'white'
                                    }}
                                >
                                    {status.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {currentStep === 2 && (
                    /* Points Slider */
                    <div className="emotion-section">
                        <label className="emotion-label">Action Points (1-10)</label>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <input
                                type="range"
                                min="1"
                                max="10"
                                value={points}
                                onChange={(e) => setPoints(parseInt(e.target.value))}
                                className="priority-slider"
                                style={{
                                    flex: 1,
                                    background: `linear-gradient(to right, #753bbd 0%, #753bbd ${(points - 1) * 11.11}%, #e0e0e0 ${(points - 1) * 11.11}%, #e0e0e0 100%)`
                                }}
                            />
                            <span style={{ minWidth: '30px', fontWeight: 'bold', color: '#753bbd' }}>{points}</span>
                            <button
                                onClick={handleSetPoints}
                                className="emotion-button"
                                style={{
                                    padding: '8px 16px',
                                    minWidth: '100px',
                                    borderColor: '#753bbd',
                                    backgroundColor: '#753bbd',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}
                            >
                                Set Points
                            </button>
                        </div>
                    </div>
                )}

                {currentStep === 3 && (
                    /* Difficulty Level */
                    <div className="emotion-section">
                        <label className="emotion-label">How difficult is this task?</label>
                        <div className="emotion-grid">
                            {DIFFICULTY_OPTIONS.map((diff) => (
                                <button
                                    key={diff.value}
                                    className={`emotion-button ${difficultyLevel === diff.value ? 'selected' : ''}`}
                                    onClick={() => handleDifficultySelect(diff.value)}
                                    style={{
                                        borderColor: difficultyLevel === diff.value ? diff.color : '#ccc',
                                        backgroundColor: difficultyLevel === diff.value ? `${diff.color}20` : 'white'
                                    }}
                                >
                                    {diff.label}
                                </button>
                            ))}
                        </div>
                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                            <button
                                onClick={handleSetDifficulty}
                                className="emotion-button"
                                disabled={!difficultyLevel}
                                style={{
                                    padding: '10px 24px',
                                    minWidth: '140px',
                                    borderColor: difficultyLevel ? '#753bbd' : '#ccc',
                                    backgroundColor: difficultyLevel ? '#753bbd' : '#e0e0e0',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    cursor: difficultyLevel ? 'pointer' : 'not-allowed',
                                    opacity: difficultyLevel ? 1 : 0.6
                                }}
                            >
                                Set Difficulty
                            </button>
                        </div>
                    </div>
                )}

                {currentStep === 4 && (
                    /* Preferred Times */
                    <div className="emotion-section">
                        <label className="emotion-label">When do you feel like working on this area? (Select all that apply)</label>
                        <div className="emotion-grid">
                            {TimeOptions.map((timeOption, index) => (
                                <button
                                    key={timeOption}
                                    className={`emotion-button ${preferredTimes.includes(timeOption) ? 'selected' : ''}`}
                                    onClick={() => handleTimeToggle(timeOption)}
                                    style={{
                                        borderColor: preferredTimes.includes(timeOption) ? '#753bbd' : '#ccc',
                                        backgroundColor: preferredTimes.includes(timeOption) ? '#753bbd20' : 'white'
                                    }}
                                >
                                    {TimeOptionsLabel[index]}
                                </button>
                            ))}
                        </div>
                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                            <button
                                onClick={handleUpdateSchedule}
                                className="emotion-button"
                                disabled={preferredTimes.length === 0}
                                style={{
                                    padding: '10px 24px',
                                    minWidth: '140px',
                                    borderColor: preferredTimes.length > 0 ? '#753bbd' : '#ccc',
                                    backgroundColor: preferredTimes.length > 0 ? '#753bbd' : '#e0e0e0',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    cursor: preferredTimes.length > 0 ? 'pointer' : 'not-allowed',
                                    opacity: preferredTimes.length > 0 ? 1 : 0.6
                                }}
                            >
                                Update Schedule
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Visual Indicator */}
            {emotion && priority >= 7 && (
                <div
                    className="attention-indicator"
                    style={{
                        backgroundColor: selectedEmotion?.color,
                        color: 'white'
                    }}
                >
                    ⚠️ Needs Attention
                </div>
            )}
        </div>
    );
};
